import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import getCSRAuthToken from '@/app/api/CSR/getCSRAuthToken'
import { getClient } from '@/lib/ApolloClient/getClient'
import isSSR from '@/utils/isSSR'

import { DeadpoolUser } from '~types/index'
import { relogInDeadpool } from '../services'

interface UseDeadpoolAuthProps {
  clientId: string
}

interface UseDeadpoolAuthReturn {
  customer: DeadpoolUser | null
  isInitialized: boolean
  setCustomer: (customer: DeadpoolUser | null) => void
}

/**
 * Custom hook for managing Deadpool authentication
 * Handles token-based authentication and customer state management
 */
export const useDeadpoolAuth = ({
  clientId,
}: UseDeadpoolAuthProps): UseDeadpoolAuthReturn => {
  const [customer, setCustomer] = useState<DeadpoolUser | null>(null)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)

  // AbortController ref for canceling async operations
  const operationRef = useRef<AbortController | null>(null)

  // Memoize Apollo client to prevent recreation
  const clientApollo = useMemo(
    () =>
      getClient({
        onError: (error) => console.error('Apollo Client Error:', error),
      }),
    []
  )

  // Memoize token to prevent unnecessary calls
  const token = useMemo(() => getCSRAuthToken(), [])

  // Memoized authentication operation handler
  const handleAuthenticationOperation = useCallback(async () => {
    // Cancel previous operation if still running
    if (operationRef.current) {
      operationRef.current.abort()
    }

    // Create new abort controller for this operation
    operationRef.current = new AbortController()

    try {
      // If customer is already set, mark as initialized
      if (customer?.id) {
        setIsInitialized(true)
        return
      }

      if (!clientId) {
        setIsInitialized(true)
        return
      }

      // If no token, mark as guest and initialized
      if (!token) {
        setIsInitialized(true)
        return
      }

      // Attempt to relogin with existing token
      const onComplete = (customerData: DeadpoolUser | null) => {
        // Check if operation was aborted before updating state
        if (operationRef.current?.signal.aborted) return

        if (token && !customerData) {
          console.error('Customer data is required when token exists')
          setIsInitialized(true)
          return
        }

        setCustomer(customerData)

        if (!isSSR()) {
          sessionStorage.setItem('customer', JSON.stringify(customerData))
        }

        setIsInitialized(true)
      }

      const onError = (error: Error) => {
        // Check if operation was aborted before updating state
        if (operationRef.current?.signal.aborted) return

        console.error('Deadpool authentication error:', error)
        setIsInitialized(true)
      }

      await relogInDeadpool(clientApollo, onComplete, onError)
    } catch (error) {
      // Check if operation was aborted before updating state
      if (operationRef.current?.signal.aborted) return

      console.error('Authentication operation failed:', error)
      setIsInitialized(true)
    }
  }, [clientId, token, customer?.id, clientApollo])

  // Effect to handle authentication with proper cleanup
  useEffect(() => {
    handleAuthenticationOperation()

    // Cleanup function to abort any pending operations
    return () => {
      if (operationRef.current) {
        operationRef.current.abort()
      }
    }
  }, [handleAuthenticationOperation])

  return {
    customer,
    isInitialized,
    setCustomer,
  }
}
